export interface RememberedLogin {
    email: string;
    password: string;
    remember: boolean;
}

const REMEMBERED_LOGIN_KEY = "primum.auth.rememberedLogin";
const LEGACY_REMEMBERED_LOGIN_KEY = "primum.rememberedLogin";
const REMEMBERED_LOGIN_COOKIE = "primum_auth_remembered_login";
const REMEMBERED_LOGIN_MAX_AGE_SECONDS = 60 * 60 * 24 * 180;

const emptyRememberedLogin = (): RememberedLogin => ({
    email: "",
    password: "",
    remember: false,
});

const canUseDocumentCookie = () => typeof document !== "undefined";

const getWindowLocalStorage = () => {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        return window.localStorage;
    } catch {
        return null;
    }
};

const normalizeRememberedLogin = (value: unknown): RememberedLogin => {
    if (!value || typeof value !== "object") {
        return emptyRememberedLogin();
    }

    const candidate = value as Partial<RememberedLogin>;

    return {
        email: typeof candidate.email === "string" ? candidate.email : "",
        password: typeof candidate.password === "string" ? candidate.password : "",
        remember: Boolean(candidate.remember),
    };
};

const parseRememberedLogin = (rawValue: string | null): RememberedLogin => {
    if (!rawValue) {
        return emptyRememberedLogin();
    }

    try {
        return normalizeRememberedLogin(JSON.parse(rawValue));
    } catch {
        return emptyRememberedLogin();
    }
};

const getLocalStorageItem = (key: string) => {
    const storage = getWindowLocalStorage();

    if (!storage) {
        return null;
    }

    try {
        return storage.getItem(key);
    } catch {
        return null;
    }
};

const setLocalStorageItem = (key: string, value: string) => {
    const storage = getWindowLocalStorage();

    if (!storage) {
        return false;
    }

    try {
        storage.setItem(key, value);
        return true;
    } catch {
        return false;
    }
};

const removeLocalStorageItem = (key: string) => {
    const storage = getWindowLocalStorage();

    if (!storage) {
        return;
    }

    try {
        storage.removeItem(key);
    } catch {
        // Some mobile/private browser modes expose localStorage but throw when used.
    }
};

const getCookieValue = (name: string) => {
    if (!canUseDocumentCookie()) {
        return null;
    }

    try {
        const cookie = document.cookie
            .split("; ")
            .find((entry) => entry.startsWith(`${name}=`));

        return cookie ? decodeURIComponent(cookie.slice(name.length + 1)) : null;
    } catch {
        return null;
    }
};

const setCookieValue = (name: string, value: string) => {
    if (!canUseDocumentCookie()) {
        return;
    }

    try {
        const secureFlag = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
        document.cookie = `${name}=${encodeURIComponent(value)}; Max-Age=${REMEMBERED_LOGIN_MAX_AGE_SECONDS}; Path=/; SameSite=Lax${secureFlag}`;
    } catch {
        // Cookies can be disabled by browser policy; localStorage remains the primary store.
    }
};

const removeCookieValue = (name: string) => {
    if (!canUseDocumentCookie()) {
        return;
    }

    try {
        document.cookie = `${name}=; Max-Age=0; Path=/; SameSite=Lax`;
    } catch {
        // Cookies can be disabled by browser policy.
    }
};

export const clearRememberedLogin = () => {
    removeLocalStorageItem(REMEMBERED_LOGIN_KEY);
    removeLocalStorageItem(LEGACY_REMEMBERED_LOGIN_KEY);
    removeCookieValue(REMEMBERED_LOGIN_COOKIE);
};

export const saveRememberedLogin = (value: Partial<RememberedLogin>) => {
    const rememberedLogin = normalizeRememberedLogin(value);

    if (!rememberedLogin.remember) {
        clearRememberedLogin();
        return;
    }

    const serializedValue = JSON.stringify({
        email: rememberedLogin.email.trim(),
        password: rememberedLogin.password,
        remember: true,
    });

    setLocalStorageItem(REMEMBERED_LOGIN_KEY, serializedValue);
    setCookieValue(REMEMBERED_LOGIN_COOKIE, serializedValue);

    removeLocalStorageItem(LEGACY_REMEMBERED_LOGIN_KEY);
};

export const getRememberedLogin = (): RememberedLogin => {
    const persistedValue = parseRememberedLogin(getLocalStorageItem(REMEMBERED_LOGIN_KEY));

    if (persistedValue.remember) {
        return persistedValue;
    }

    const cookieValue = parseRememberedLogin(getCookieValue(REMEMBERED_LOGIN_COOKIE));

    if (cookieValue.remember) {
        saveRememberedLogin(cookieValue);
        return cookieValue;
    }

    const legacyValue = parseRememberedLogin(getLocalStorageItem(LEGACY_REMEMBERED_LOGIN_KEY));

    if (legacyValue.remember) {
        saveRememberedLogin(legacyValue);
        return legacyValue;
    }

    return emptyRememberedLogin();
};
